<template>
  <div class="news-detail-container">
    <!-- 加载状态 -->
    <el-loading
      v-if="loading"
      fullscreen
      text="正在加载新闻详情..."
      spinner="el-icon-loading"
      background="rgba(255,255,255,0.9)"
    ></el-loading>

    <!-- 错误提示 -->
    <div v-else-if="error" class="error-container">
      <el-alert
        title="加载失败"
        :description="error"
        type="error"
        show-icon
      ></el-alert>
      <el-button 
        type="primary" 
        @click="loadNewsDetail"
        style="margin-top: 20px;"
      >
        重新加载
      </el-button>
    </div>

    <!-- 新闻详情内容 -->
    <div v-else-if="newsDetail" class="news-content">
      <!-- 返回按钮 -->
      <el-button 
        type="text" 
        @click="$router.go(-1)"
        class="back-button"
      >
        < 返回列表
      </el-button>

      <!-- 新闻标题 -->
      <h1 class="news-title">{{ newsDetail.newsTitle }}</h1>

      <!-- 新闻元信息 -->
      <div class="news-meta">
        <span class="publish-time">
          <i class="el-icon-time"></i>
          {{ formatTime(newsDetail.publishTime) }}
        </span>
        <span class="news-source" v-if="newsDetail.newsSource">
          <i class="el-icon-document"></i>
          来源：{{ newsDetail.newsSource }}
        </span>
        <span class="news-type-tag" :style="{backgroundColor: getTypeColor(newsDetail.newsType)}">
          {{ newsDetail.newsType }}
        </span>
      </div>


      <!-- 新闻内容（支持图文混排） -->
      <div class="news-body" v-html="formatNewsContent(newsDetail.newsContent)"></div>
    </div>

    <!-- 未找到新闻 -->
    <el-empty
      v-else
      description="未找到该新闻详情"
      class="empty-state"
    >
      <el-button 
        type="primary" 
        @click="$emit('back')"
      >
        返回列表
      </el-button>
    </el-empty>
  </div>
</template>

<script>
export default {
  // 通过props接收新闻ID
  props: {
    id: {
      type: String,
      required: true,
      description: '新闻ID，用于获取详情'
    }
  },
  data() {
    return {
      newsDetail: null, // 新闻详情
      loading: false, // 加载状态
      error: '', // 错误信息
      allNews: [] // 所有新闻数据
    }
  },
  watch: {
    // 监听id变化，重新加载数据
    id: {
      immediate: true, // 初始化时立即执行
      handler() {
        this.loadNewsDetail()
      }
    }
  },
  methods: {
    // 加载新闻详情
    async loadNewsDetail() {
      // 验证ID是否有效
      if (!this.id) {
        this.error = '新闻ID无效'
        return
      }

      this.loading = true
      this.error = ''
      try {
        const response = await this.$httpTemp.get('/mock/news.json')
        this.allNews = response
        
        // 根据ID查找对应的新闻
        const foundNews = this.allNews.find(news => news.id === this.id)
        if (!foundNews) {
          throw new Error('未找到该新闻')
        }
        
        // 只允许查看类型2的新闻详情
        if (foundNews.newsCategory !== 2) {
          throw new Error('该新闻不支持查看详情')
        }
        
        this.newsDetail = foundNews
      } catch (err) {
        this.error = err.message || '新闻详情加载失败，请稍后重试'
        console.error('加载新闻详情错误：', err)
      } finally {
        this.loading = false
      }
    },
    // 格式化发布时间
    formatTime(timeStr) {
      return timeStr ? timeStr.slice(0, 19).replace('T', ' ') : '未知时间'
    },
    // 处理封面图加载失败
    handleCoverError(e) {
      e.target.src = 'https://picsum.photos/800/400?grayscale'
    },
    // 获取新闻类型标签颜色
    getTypeColor(type) {
      const colorMap = {
        科技: '#409EFF',
        自然: '#52C41A',
        金融: '#FAAD14',
        体育: '#EB2F96',
        教育: '#722ED1'
      }
      return colorMap[type] || '#9CA3AF'
    },
    formatNewsContent(content) {
      if (!content) return ''
      // 步骤1：处理制表符\t -> 转换为首行缩进2字符（通过后续CSS实现）
      // 先移除内容中可能存在的\t，避免干扰格式
      let formatted = content.replace(/\t/g, '')
      // 步骤2：处理换行符\n -> 转换为<br>
      formatted = formatted.replace(/\r?\n/g, '<br>')
      // 步骤3：按<br>分割为段落，为每个段落添加首行缩进标记
      const paragraphs = formatted.split('<br>')
        .filter(para => para.trim()) // 过滤空内容
        .map(para => `<p class="content-paragraph">${para}</p>`)
        .join('')
      
      // 步骤4：为图片添加响应式样式
      return paragraphs.replace(/<img/g, '<img class="content-image"')
    },

  }
}
</script>

<style scoped>
/* 样式与之前保持一致 */
.news-detail-container {
  max-width: 80%;
  margin: 0 auto;
  padding: 20px;
  font-family: "Microsoft YaHei", sans-serif;
}

.error-container {
  text-align: center;
  padding: 50px 0;
}

.empty-state {
  padding: 100px 0;
}

.back-button {
  margin-bottom: 20px;
  display: inline-block;
}

.news-title {
  font-size: 32px; /* 从28px增大到32px */
  color: #1F2937;
  font-weight: 700;
  line-height: 1.5; /* 适当增大行高 */
  margin: 0 0 25px 0;
  text-align: center;
}

.news-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  justify-content: center;
  color: #6B7280;
  font-size: 15px; /* 从14px增大到15px */
  margin-bottom: 35px; /* 增加与正文的间距 */
  padding-bottom: 15px;
  border-bottom: 1px solid #E5E7EB;
}

.news-meta i {
  margin-right: 5px;
}

.news-type-tag {
  padding: 3px 10px;
  border-radius: 12px;
  color: #fff;
  font-size: 12px;
}


.news-body {
  color: #374151;
  font-size: 18px; /* 从16px增大到18px */
  line-height: 2.0; /* 从1.8增大到2.0 */
  padding: 0 10px; /* 增加左右内边距，避免内容贴边 */
}

.news-body p {
  margin: 0 0 20px 0;
  text-align: justify;
}
.news-body br {
  line-height: 2.0; /* 与正文行高保持一致 */
}
.content-image {
  max-width: 100%;
  height: auto;
  margin: 25px auto; /* 从20px增大到25px */
  display: block;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
@media (max-width: 768px) {
  .news-title {
    font-size: 22px;
  }
  
  .news-body {
    font-size: 15px;
  }
  
  .news-meta {
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
}
::v-deep .content-paragraph {
  text-indent: 2em;
  margin: 0 0 16px 0;
}
/* 图片居中展示优化 */
::v-deep .content-image {
  /* 核心居中设置 */
  display: block;         /* 转为块级元素 */
  margin-left: auto;      /* 左外边距自动 */
  margin-right: auto;     /* 右外边距自动 */
  
  /* 响应式设置 */
  max-width: 100%;        /* 最大宽度不超过父容器 */
  height: auto;           /* 高度自适应，保持比例 */
  
  /* 间距与美化 */
  margin-top: 25px;       /* 上间距 */
  margin-bottom: 25px;    /* 下间距 */
  border-radius: 4px;     /* 圆角 */
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15); /* 增强阴影效果 */
  
  /* 确保图片不会超出容器 */
  max-height: 600px;      /* 限制最大高度，避免过长图片 */
  object-fit: contain;    /* 保持图片完整显示 */
}

</style>
 